import React, { useState } from "react";
import { Folder, ChevronDown, ChevronRight } from "lucide-react";
import TerraformIcon from "./TerraformIcon";
import { cn } from "../../../../utils";

export interface FileNode {
  name: string;
  type: "file" | "folder";
  path?: string;
  file_path?: string;
  icon?: React.ReactNode;
  children?: FileNode[];
  content?: string;
  node?: "generate_module" | "generate_terraform" | string;
}

interface FileTreeProps {
  files: FileNode[];
  onFileSelect: (path: string) => void;
  selectedPath?: string;
}

const FileTreeNode: React.FC<{
  node: FileNode;
  depth: number;
  onFileSelect: (path: string) => void;
  selectedPath?: string;
}> = ({ node, depth, onFileSelect, selectedPath }) => {
  const [isOpen, setIsOpen] = useState(true);

  const toggleFolder = () => {
    setIsOpen(!isOpen);
  };

  const isSelected = node.path === selectedPath;

  // Render file or folder
  if (node.type === "file") {
    return (
      <div
        className={cn(
          "flex items-center py-1 px-2 gap-2 cursor-pointer text-textsecondaryLight hover:bg-hoverLight rounded outline-none focus-visible:ring-2 focus-visible:ring-blue-400",
          isSelected && "bg-selectedLight text-textprimaryLight font-semibold"
        )}
        style={{ paddingLeft: `${(depth + 1) * 18}px`, maxWidth: "200px" }}
        tabIndex={0}
        onClick={() => node.path && onFileSelect(node.path)}
      >
        {node.icon || (
          <TerraformIcon className="h-4 w-4 text-iconaccentLight mr-2" />
        )}
        <span className="text-sm truncate max-w-[160px]">{node.name}</span>
      </div>
    );
  }

  return (
    <div>
      <div
        className={cn(
          "flex items-center py-1 px-2 cursor-pointer rounded outline-none focus-visible:ring-2 focus-visible:ring-blue-400 group transition-colors",
          isSelected
            ? "bg-selectedLight text-black"
            : "text-black hover:bg-blue-50"
        )}
        style={{ paddingLeft: `${depth * 18}px`, maxWidth: "200px" }}
        tabIndex={0}
        onClick={toggleFolder}
      >
        {isOpen ? (
          <ChevronDown className="h-4 w-4 text-divider mr-1" />
        ) : (
          <ChevronRight className="h-4 w-4 text-divider mr-1" />
        )}
        <Folder
          className={cn(
            "h-4 w-4 mr-2 transition-colors",
            isSelected
              ? "text-gray-600"
              : "text-gray-400 group-hover:text-gray-500"
          )}
        />
        <span className="text-sm truncate max-w-[160px]">{node.name}</span>
      </div>

      {isOpen && node.children && (
        <div className="file-tree-children">
          {node.children.map((child, index) => (
            <FileTreeNode
              key={`${child.name}-${index}`}
              node={child}
              depth={depth + 1}
              onFileSelect={onFileSelect}
              selectedPath={selectedPath}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const FileTree: React.FC<FileTreeProps> = ({
  files,
  onFileSelect,
  selectedPath,
}) => {
  return (
    <div className="file-tree w-64 shrink-0 flex flex-col h-full min-h-0 overflow-y-auto bg-panelLight border-r border-dividerLight">
      {/* Scrollable list area */}
      <div className="pt-4 overflow-y-auto flex-1 min-h-0 px-2">
        {files.map((node, index) => (
          <FileTreeNode
            key={`${node.name}-${index}`}
            node={node}
            depth={0}
            onFileSelect={onFileSelect}
            selectedPath={selectedPath}
          />
        ))}
      </div>
    </div>
  );
};

export default FileTree;
